import * as React from "react";
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {IfBox} from "../../../../../style/if";
import {Button, Label} from "semantic-ui-react";
import {CreateDefaultConfigModal} from "../modals/CreateDefaultConfigModal";
import {NavTreeNavigation} from "./NavTreeNavigation";
import {NavTreeDetails} from "./NavTreeDetails";
import {ConfigContext} from "../../../../context/ConfigContext";
import {configStorage} from "../../../../../../ConfigStorage";
import {GetTypeRequest, GetTypeResponse} from "../../../../../../generated";
import {NavTreeContextMenu} from "../modals/NavTreeContextMenu";
import {OtherConfigFieldImplementationsModal} from "../modals/OtherConfigFieldImplementationsModal";
import {Signal, signal} from "@preact/signals-react";
import {updateConfig, workingConfig} from "../../ConfigSignal";
import {useEffect} from "react";
import {
    configContainsSearchMatch,
    hasSearchQuery,
    nodeMatchesSearch,
    normalizeSearchQuery
} from "./NavTreeSearch";

const swagger = require("../../../../../../../../swaggerInternal.json")

export interface PathWithDescription {
    path: string
    description: string
}
export const focusesOnField: Signal<PathWithDescription> = signal(null)
export const responsibleLogeniosManagerOptions = signal<{ key: string, text: string, value: string }[]>([])
export const shadowCursor = signal({path: "$", name: "CompanyConfiguration"})
export async function fetchResponsibleLogeniosManagers() {
    console.log("fetchResponsibleLogeniosManagers")
    function stringToOption(str: string) {
        return {key: str, text: str, value: str}
    }
    let auth = backend.withTokenAuthHeader(authentication.token)
    let response = await backend.internalApi.getSuperUserTwoFactorAuthenticationStatus(auth)
    return response.superUsersList.map(su => {
        return stringToOption(su.userEmail!)
    }).sort((a, b) => {
        if (a.text.endsWith("@logenios.com") && !b.text.endsWith("@logenios.com")) {
            return -1;
        }
        if (!a.text.endsWith("@logenios.com") && b.text.endsWith("@logenios.com")) {
            return 1;
        }
        return a.text.localeCompare(b.text);
    })
}


export const openInModalPaths: Signal<string[]> = signal([])
export async function loadOpenInModalPaths() {
    let auth = backend.withTokenAuthHeader(authentication.token)
    openInModalPaths.value = (await backend.internalApi.fetchCmNavTreeTextModalList(auth)).pathList
}

export interface NavTreeProps {
    saveKeyListener: (e: any) => void
    changeTab: (index: number, scriptPath: string) => void
    auth: any
}

export function NavTree(props: React.PropsWithChildren<NavTreeProps>) {

    const context = React.useContext(ConfigContext)

    const [showCreateConfigModal, setShowCreateConfigModal] = React.useState(false)
    const [cursor, setCursor] = React.useState<any>({path: "$", name: "CompanyConfiguration"})
    const [data, setData] = React.useState<any>(null)
    const [values, setValues] = React.useState<any>(null)

    const [contextMenuOpen, setContextMenuOpen] = React.useState(false)
    const [contextMenuAnchorPoint, setContextMenuAnchorPoint] = React.useState({x: 0, y: 0})
    const [showDescriptionFor, setShowDescriptionFor] = React.useState<any>("")

    const [modalToOpen, setModalToOpen] = React.useState<any>(null)

    useEffect(() => {
        shadowCursor.value = cursor
    }, [cursor]);

    function rightClickHandler(event) {
        // @ts-ignore
        if(event.target.id == "withCustomContextMenu") {
            event.preventDefault();
            // @ts-ignore
            console.log(event.target.innerText);
            // @ts-ignore
            let name = event.target.innerText
            setContextMenuAnchorPoint({x: event.x, y: event.y})
            let c = context.navTreeCursor
            setShowDescriptionFor(c.path + "." + c.name + "." + name)
            setContextMenuOpen(true)

        }
    }
    React.useEffect(() => {
        fetchResponsibleLogeniosManagers().then(options => {
            responsibleLogeniosManagerOptions.value = options.filter(o => o != null)
            loadOpenInModalPaths().then()
            refreshTree().then()
        })


        document.addEventListener('contextmenu',rightClickHandler);

        return () => {
            document.removeEventListener('contextmenu', rightClickHandler);
        }
    }, [])

    React.useEffect(() => {
        console.log("NavTree useEffect: workingConfig: ", workingConfig)
        refreshTree().then(r => {})
    }, [workingConfig.value])



    let showCreateConfig = false
    if (workingConfig.value == "{}") {
        //config empty
        showCreateConfig = true
    }

    if (showCreateConfig) {
        return <>
            <Label color="yellow">There is no Configuration for this company, yet.</Label>
            <br/>
            <Button type="button" onClick={() => setShowCreateConfigModal(true)}>Create new config</Button>
            <IfBox shouldShow={showCreateConfigModal}>
                <CreateDefaultConfigModal
                    isOpen={showCreateConfigModal}
                    onClose={() => setShowCreateConfigModal(false)}
                    auth={props.auth}/>
            </IfBox>
        </>
    }

    async function refreshTree() {

        let unchangedConfig = (await configStorage.getElement(context.companyId))?.unchangedConfig
        if(unchangedConfig == null) {
            unchangedConfig = await backend.internalApi.fetchCompanyConfiguration(context.companyId, props.auth)
            let unchangedConfigString = JSON.stringify(unchangedConfig, null, 4)
            await updateConfig(unchangedConfigString, true, true)
        }

        let splitData = filterOnlyObjects(
            "$",
            JSON.parse(workingConfig.value),
            [],
            context.navTreeCursor,
            context,
            unchangedConfig
        )

        let somethingHasBeenEdited = splitData.values.filter(v => {
            return v.isEdited == true;
        }).length > 0;

        let formattedData = {
            name: "CompanyConfiguration",
            path: "$",
            toggled: true,
            children: splitData.objects,
            isEdited: somethingHasBeenEdited,
        }

        setData(formattedData)
        setValues(splitData.values)


    }

    function filterOnlyObjects(path, config, previousValues, cursor, context, unchangedConfig): { objects: any[], values: any[] } {
        let objects: any[] = [];
        let values = previousValues;
        const cursorPath = cursor ? cursor.path + "." + cursor.name : ""
        const normalizedSearchQuery = normalizeSearchQuery(context.treeSearchQuery)
        const isSearchActive = hasSearchQuery(normalizedSearchQuery)

        let recursive = (p, c) => {
            return (filterOnlyObjects(p, c, values, cursor, context, unchangedConfig))
        }

        let renameListChildren = (list) => {
            //console.log("[renameListChildren] list: ", list)
            if (list.length == 0 || list[0].type != "object") {
                return list
            }

            let newList = []

            list.forEach((elem) => {
                let relevantValues = values.filter((v) => v.path == elem.path + "." + elem.name)

                if (relevantValues != null && relevantValues.length > 0) {
                    elem.displayName = findDisplayName(relevantValues)
                }
                newList.push(elem)
            })
            return newList
        }

        for (const key of Object.keys(config)) {
            let toggled = false
            let active = false
            const nodePath = path + "." + key

            if (cursorPath) {
                toggled = cursorPath == nodePath || cursorPath.startsWith(nodePath + ".")
            }

            if (isSearchActive) {
                const selfMatch = nodeMatchesSearch(key, config[key], normalizedSearchQuery)
                const subtreeMatch = configContainsSearchMatch(config[key], normalizedSearchQuery)

                if (selfMatch || subtreeMatch) {
                    toggled = true
                }
                if (selfMatch) {
                    active = true
                }
            }
            if (nodePath == "$.CompanyConfiguration") {
                toggled = true;
            }

            const valueActive = isSearchActive && nodeMatchesSearch(key, config[key], normalizedSearchQuery)

            if (config[key] == null) {
                // null
                values.push({
                    type: "null",
                    name: key,
                    path: path,
                    active: valueActive,
                    value: config[key],
                })

            } else {
                let json = JSON.stringify(config[key])

                if (json.startsWith("{")) {
                    // object
                    let creationResponse = createChildren(recursive, path, key, config)

                    let foundChange = checkForChange({
                        name: key,
                        path: path,
                        value: config[key]
                    }, unchangedConfig)

                    let o = {
                        type: "object",
                        name: key,
                        toggled: toggled,
                        active: active,
                        isEdited: foundChange,
                        path: path,
                        children: creationResponse.children.objects,

                    }
                    objects.push(o)

                } else if (json.startsWith("[")) {
                    let creationResponse = createChildren(recursive, path, key, config)
                    let foundChange = checkForChange({
                        name: key,
                        path: path,
                        value: config[key]
                    }, unchangedConfig)

                    let l = {
                        type: "list",
                        name: key,
                        toggled: toggled,
                        active: active,
                        isEdited: foundChange,
                        path: path,
                        children: renameListChildren(creationResponse.children.objects)
                    }
                    objects.push(l)
                } else {
                    // value (String, number, boolean...)
                    let foundChange = checkForChange({name: key, path: path, value: config[key]}, unchangedConfig)
                    if (json.startsWith("\"")) {
                        if(path.endsWith("sourceCompanyTag")) {
                            console.log("[sourceCompanyTag] key: ", key, " value: ", config[key], " path: ", path)
                        }
                        let type = "string"
                        try {
                            let index = parseInt(key)
                            if (!isNaN(index)) {
                                // if the key is a number, it is a list element
                                type = "valueListElement"
                            }
                        } catch (e) {

                        }

                        values.push({
                            type: type,
                            name: key,
                            path: path,
                            active: valueActive,
                            isEdited: foundChange,
                            value: config[key]
                        })

                    } else if (json == "true" || json == "false") {
                        // boolean
                        let foundChange = checkForChange({name: key, path: path, value: config[key]}, unchangedConfig)
                        values.push({
                            type: "boolean",
                            name: key,
                            path: path,
                            active: valueActive,
                            isEdited: foundChange,
                            value: config[key],
                        })

                    } else {
                        // number?!
                        let foundChange = checkForChange({name: key, path: path, value: config[key]}, unchangedConfig)
                        values.push({
                            type: "number",
                            name: key,
                            path: path,
                            active: valueActive,
                            isEdited: foundChange,
                            value: config[key]
                        })
                    }
                }
            }
        }

        return {objects: objects, values: values}
    }

    function checkForChange(element,unchangedConfig) {
        const jp = require('jsonpath');
        if(unchangedConfig != undefined) {
            let unchangedValue = jp.value(unchangedConfig, element.path + "." + element.name)
            return JSON.stringify(unchangedValue) != JSON.stringify(element.value)
        } else {
            return false
        }
    }

    function createChildren(recursive, path, key, config) {
        let nextPath = path + "." + key;
        let children = recursive(nextPath, config[key])
        let isChildEdited = false

        //checking if at least one of the sub objects has been edited
        if (children.objects) {
            let editedChildren = children.objects.filter(o => {
                return o.isEdited == true && o.path == nextPath;
            })
            //console.log("editedChildren: ", editedChildren)
            isChildEdited = editedChildren.length > 0
        }

        //if none of the sub objects has been edited, check if one the values has been edited
        if (children.values != undefined) {
            let editedChildren = children.values.filter(v => {
                return v.isEdited == true && v.path == nextPath;
            })

            //console.log("editedChildren: ", editedChildren)
            isChildEdited = editedChildren.length > 0
        }

        return {children: children, isChildEdited: isChildEdited}
    }

    async function fixTypeDefinitionIfEnum(element, response) {

        if (/^\d+$/.test(element.key)) {
            console.log("key is element of array ")
        } else {
            if (response.isEnum) {
                element.type = "enum"
                element.enumOptions = JSON.parse(response.defaultObject)
                //console.log("FIXED DEF FOR " + element.name)
            }
            if (response.isValueList) {
                element.type = "valueListElement"

                console.log("[DEBUG] fixTypeDefinitionIfEnum: ", element.name, response)
                // get Type from Swagger
                let pathArray = element.path.split(".")
                let lastName = pathArray.pop()
                let path = pathArray.join(".")
                if(pathArray.length == 1) {
                    path = element.path
                }
                let previousResponse = (await getTypeByPath(path))


                let type = previousResponse.type.split("$").pop().split(".").pop()

                if (type == "List") {
                    type = previousResponse.listType.split(".").pop()
                }

                let jp = require('jsonpath');
                let jsonPath = "$.definitions." + type + ".properties." + lastName + ".items.enum"
                let obj = jp.query(swagger, jsonPath);

                element.enumOptions = obj[0]

                //console.log("FIXED DEF FOR " + element.name)
            }
        }
        return true
    }

    function getType(element): Promise<GetTypeResponse> {
        return getTypeByPath(element.path + "." + element.name)
    }

    function getTypeByPath(path: string): Promise<GetTypeResponse> {
        let request = {} as GetTypeRequest
        request.config = JSON.parse(workingConfig.value)
        request.path = path
        return backend.internalApi.getType(request, props.auth)
    }

    return <div className={"flexRow"}>
        <NavTreeNavigation
            cursor={cursor}
            updateCursor={setCursor}
            data={data}
            updateData={setData}
            values={values}
            refreshTree={refreshTree}
            fixTypeDefinitionIfEnum={fixTypeDefinitionIfEnum}

        />

        <NavTreeDetails
            cursor={cursor}
            values={values}
            refreshTree={refreshTree}
            updateValues={setValues}
            fixTypeDefinitionIfEnum={fixTypeDefinitionIfEnum}
            getType={getType}
            saveKeyListener={props.saveKeyListener}
        />

        {contextMenuOpen && <NavTreeContextMenu
                isOpen={contextMenuOpen}
                onClose={(mto) => {
                    setContextMenuOpen(false)
                    if (mto) {
                        setModalToOpen(mto)
                    }
                }}
                x={contextMenuAnchorPoint.x}
                y={contextMenuAnchorPoint.y}
                values={values}
                fieldName={showDescriptionFor}
                changeTab={props.changeTab}
        />
        }

        {modalToOpen == "other implementations" &&
            <OtherConfigFieldImplementationsModal
                isOpen={modalToOpen == "other implementations"}
                onClose={() => setModalToOpen(null)}
                path={showDescriptionFor}
                notNull={true}
                isContract={false}
            />
        }

        {modalToOpen == "other implementations matching value" &&
            <OtherConfigFieldImplementationsModal
                isOpen={modalToOpen == "other implementations matching value"}
                onClose={() => setModalToOpen(null)}
                path={showDescriptionFor}
                matchValue={values.find(v => (v.path + "." + v.name) == normalizePath(showDescriptionFor))?.value}
                notNull={false}
                isContract={false}
            />
        }
    </div>

}

export function normalizePath(path: string): string {
    return path.replace("CompanyConfiguration.", "")
}

export function findDisplayName(values: any[]): string {
    let displayNames = [
        "verboseName",
        "api",
        "key",
        "etaSource",
        "description",
        "ruleName",
        "lisStatusCode",
        "name",
        "columnName",
        "displayName",
        "defaultLabel"
    ]

    for (let displayName of displayNames) {
        let foundValue = values.find((v) => v.name === displayName)
        if (foundValue && foundValue.value != null) {
            return foundValue.value.toString()
        }
    }

    return undefined
}


export function cleanPath(path: string){
    let splitPath = path.split(".")
    let cleanSplitPath = []
    splitPath.forEach(part => {
        if(part != "CompanyConfiguration") {
            cleanSplitPath.push(part)
        }
    })
    return cleanSplitPath.join(".")
}